import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Tv, Smartphone, Monitor, Star, Copy, Check, Info, X } from 'lucide-react';

const API_URL = '';

// Logo Cardoso Tech
const CARDOSO_LOGO_URL = 'https://customer-assets.emergentagent.com/job_salebotflow/artifacts/yxgkf7f7_IMG_4355.png';

// URLs das logos dos apps
const APP_LOGOS = {
  'cartec': CARDOSO_LOGO_URL,
  'brasil-iptv': 'https://hplay.in/control/actions/imagens_app/300725100801.png',
  'xcloud-tv': 'https://hplay.in/control/actions/imagens_app/300725101730.png',
  'xciptv': 'https://hplay.in/control/actions/imagens_app/150925022452.png',
  'iptv-smarters': 'https://hplay.in/control/actions/imagens_app/250825071603.png',
};

// Componente para copiar código
const CopyButton = ({ code }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.log('Código:', code);
    }
  };
  
  if (!code) return null;
  
  return (
    <button
      onClick={handleCopy}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: '20px 32px',
        backgroundColor: copied ? '#22c55e' : '#f97316',
        border: 'none',
        borderRadius: '16px',
        color: 'white',
        fontSize: '20px',
        fontWeight: 'bold',
        cursor: 'pointer',
        minHeight: '70px',
        width: '100%',
        maxWidth: '400px',
        transition: 'all 0.2s',
        outline: 'none'
      }}
    >
      {copied ? <Check size={28} /> : <Copy size={28} />}
      <span>Downloader: {code}</span>
      {copied && <span style={{fontSize: '16px', opacity: 0.8}}>(Copiado!)</span>}
    </button>
  );
};

// Botão de Download
const DownloadButton = ({ url, size, featured }) => {
  const [downloading, setDownloading] = useState(false);
  
  const handleDownload = () => {
    if (!url || url === '#' || size === 0) {
      alert('APK em breve disponível!');
      return;
    }
    setDownloading(true);
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setDownloading(false), 3000);
  };
  
  return (
    <button
      onClick={handleDownload}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: featured ? '20px 32px' : '16px 24px',
        background: downloading 
          ? 'linear-gradient(to right, #22c55e, #16a34a)' 
          : 'linear-gradient(to right, #7c3aed, #8b5cf6)',
        border: 'none',
        borderRadius: '16px',
        color: 'white',
        fontSize: featured ? '22px' : '18px',
        fontWeight: 'bold',
        cursor: 'pointer',
        minHeight: featured ? '70px' : '60px',
        width: '100%',
        maxWidth: '400px',
        transition: 'all 0.2s',
        boxShadow: '0 10px 30px rgba(124, 58, 237, 0.3)',
        outline: 'none'
      }}
    >
      <Download size={featured ? 28 : 22} />
      <span>{downloading ? 'BAIXANDO...' : 'BAIXAR'}</span>
      {size > 0 && <span style={{opacity: 0.8, fontWeight: 'normal'}}>| {size} MB</span>}
    </button>
  );
};

// Card do App em Destaque (Cardoso TV)
const FeaturedAppCard = ({ app, isFocused, onFocus }) => {
  const cardRef = useRef(null);
  const downloadUrl = app.apk_filename && app.size_mb > 0
    ? `${API_URL}/api/download/${encodeURIComponent(app.apk_filename)}`
    : '#';
  
  const hasDownloader = app.downloader_code && app.downloader_code.trim() !== '';

  useEffect(() => {
    if (isFocused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isFocused]);

  return (
    <div
      ref={cardRef}
      data-index={0}
      onClick={() => onFocus(0)}
      style={{
        position: 'relative',
        backgroundColor: '#1e1b4b',
        borderRadius: '24px',
        border: isFocused ? '3px solid #a78bfa' : '2px solid #3730a3',
        padding: '32px',
        marginBottom: '24px',
        transition: 'all 0.2s',
        boxShadow: isFocused ? '0 0 40px rgba(167, 139, 250, 0.4)' : '0 0 20px rgba(124, 58, 237, 0.2)',
        transform: isFocused ? 'scale(1.01)' : 'scale(1)',
        cursor: 'pointer'
      }}
      tabIndex={0}
      onFocus={() => onFocus(0)}
    >
      {/* Badge Destaque */}
      <div style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        background: 'linear-gradient(to right, #a78bfa, #8b5cf6)',
        borderRadius: '30px',
        color: 'white',
        fontSize: '14px',
        fontWeight: 'bold'
      }}>
        <Star size={18} fill="currentColor" />
        DESTAQUE
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '32px',
        flexWrap: 'wrap'
      }}>
        {/* Logo CARDOSO TECH */}
        <div style={{
          flexShrink: 0,
          width: '200px',
          height: '200px',
          borderRadius: '24px',
          overflow: 'hidden',
          backgroundColor: '#000',
          border: isFocused ? '3px solid #a78bfa' : '2px solid #4c1d95',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img 
            src={CARDOSO_LOGO_URL} 
            alt="Cardoso TV"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: '12px'
            }}
          />
        </div>
        
        {/* Info */}
        <div style={{ flex: 1, minWidth: '280px' }}>
          <h2 style={{
            fontSize: '42px',
            fontWeight: '900',
            color: 'white',
            marginBottom: '8px'
          }}>
            {app.name}
          </h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
            color: '#c4b5fd',
            fontSize: '16px'
          }}>
            {app.version && (
              <span style={{
                padding: '4px 12px',
                backgroundColor: '#3730a3',
                borderRadius: '8px'
              }}>
                v{app.version}
              </span>
            )}
            {app.updated_at && <span>Atualizado: {app.updated_at}</span>}
          </div>
          
          <p style={{
            color: '#c4b5fd',
            fontSize: '20px',
            marginBottom: '24px',
            lineHeight: 1.5
          }}>
            {app.description}
          </p>
          
          {/* Botões */}
          <div style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <DownloadButton 
              url={downloadUrl} 
              size={app.size_mb} 
              featured={true}
            />
            
            {hasDownloader && <CopyButton code={app.downloader_code} />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Card de App Normal (outros apps)
const AppCard = ({ app, index, isFocused, onFocus }) => {
  const cardRef = useRef(null);
  const downloadUrl = app.apk_filename && app.size_mb > 0
    ? `${API_URL}/api/download/${encodeURIComponent(app.apk_filename)}`
    : '#';
  
  const hasDownloader = app.downloader_code && app.downloader_code.trim() !== '';
  const logoUrl = APP_LOGOS[app.slug] || CARDOSO_LOGO_URL;

  useEffect(() => {
    if (isFocused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isFocused]);

  return (
    <div
      ref={cardRef}
      data-index={index}
      onClick={() => onFocus(index)}
      style={{
        backgroundColor: '#111827',
        borderRadius: '20px',
        border: isFocused ? '3px solid #a78bfa' : '2px solid #1f2937',
        padding: '24px',
        marginBottom: '16px',
        transition: 'all 0.2s',
        boxShadow: isFocused ? '0 0 30px rgba(167, 139, 250, 0.3)' : 'none',
        cursor: 'pointer'
      }}
      tabIndex={0}
      onFocus={() => onFocus(index)}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        flexWrap: 'wrap'
      }}>
        {/* Logo */}
        <div style={{
          flexShrink: 0,
          width: '100px',
          height: '100px',
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: '#000',
          border: '2px solid #374151'
        }}>
          <img 
            src={logoUrl} 
            alt={app.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: '8px'
            }}
          />
        </div>
        
        {/* Info */}
        <div style={{ flex: 1, minWidth: '200px' }}>
          <h3 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '8px'
          }}>
            {app.name}
          </h3>
          
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <DownloadButton 
              url={downloadUrl} 
              size={app.size_mb}
              featured={false}
            />
            
            {hasDownloader ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard?.writeText(app.downloader_code);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px 20px',
                  backgroundColor: '#f97316',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                <Tv size={20} />
                {app.downloader_code}
              </button>
            ) : (
              <span style={{
                padding: '16px 20px',
                backgroundColor: '#374151',
                borderRadius: '12px',
                color: '#9ca3af',
                fontSize: '16px'
              }}>
                Sem código
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal de Instruções
const InstructionsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      backgroundColor: 'rgba(0,0,0,0.9)'
    }}>
      <div style={{
        backgroundColor: '#1e1b4b',
        borderRadius: '24px',
        border: '2px solid #3730a3',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '32px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '32px',
            fontWeight: '900',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Info size={32} color="#a78bfa" />
            Como Instalar
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '12px',
              backgroundColor: '#3730a3',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            <X size={24} />
          </button>
        </div>
        
        {[
          { n: 1, title: 'Baixe o Downloader', desc: 'Na Play Store da sua TV, busque e instale o app "Downloader"' },
          { n: 2, title: 'Digite o Código', desc: 'Abra o Downloader e digite o código numérico do app' },
          { n: 3, title: 'Instale o APK', desc: 'O download inicia automaticamente. Clique em "Instalar"' },
          { n: 4, title: 'Permita Instalação', desc: 'Se pedir, vá em Configurações → Segurança → Fontes desconhecidas' }
        ].map((step) => (
          <div key={step.n} style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '24px',
            alignItems: 'flex-start'
          }}>
            <div style={{
              flexShrink: 0,
              width: '50px',
              height: '50px',
              backgroundColor: '#7c3aed',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white'
            }}>
              {step.n}
            </div>
            <div>
              <p style={{ fontSize: '22px', fontWeight: 'bold', color: 'white', marginBottom: '4px' }}>
                {step.title}
              </p>
              <p style={{ fontSize: '18px', color: '#c4b5fd' }}>
                {step.desc}
              </p>
            </div>
          </div>
        ))}
        
        <button
          onClick={onClose}
          autoFocus
          style={{
            width: '100%',
            padding: '20px',
            backgroundColor: '#7c3aed',
            border: 'none',
            borderRadius: '16px',
            color: 'white',
            fontSize: '22px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginTop: '16px'
          }}
        >
          ENTENDI
        </button>
      </div>
    </div>
  );
};

// Página Cardoso Tech
const CardosoPage = () => {
  const [featuredApp, setFeaturedApp] = useState(null);
  const [otherApps, setOtherApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/apps`);
      if (response.ok) {
        const data = await response.json();
        const allApps = data.apps || [];
        
        // Cardoso TV em destaque
        const cardoso = allApps.find(a => a.slug === 'cartec');
        if (cardoso) {
          setFeaturedApp(cardoso);
        }
        
        // Outros apps (MENOS mn-tech e cartec)
        const others = allApps.filter(a => 
          a.slug !== 'cartec' && 
          a.slug !== 'mn-tech' && 
          !a.owner // Não mostrar apps de outros revendedores
        );
        setOtherApps(others);
      }
    } catch (error) {
      console.error('Erro ao carregar apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalApps = (featuredApp ? 1 : 0) + otherApps.length;

  // Navegação por teclado
  const handleKeyDown = useCallback((e) => {
    if (showInstructions) {
      if (e.key === 'Escape' || e.key === 'Enter' || e.key === 'Backspace') {
        setShowInstructions(false);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => Math.max(0, prev - 1));
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => Math.min(totalApps - 1, prev + 1));
        break;
      case 'Enter':
      case 'Select':
        e.preventDefault();
        const btn = document.querySelector(`[data-index="${focusedIndex}"] button`);
        if (btn) btn.click();
        break;
      default:
        break;
    }
  }, [showInstructions, totalApps, focusedIndex]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0f0a1f',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header com logo CARDOSO TECH */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'rgba(15, 10, 31, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #3730a3',
        padding: '16px 20px'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <img 
            src={CARDOSO_LOGO_URL} 
            alt="Cardoso Tech" 
            style={{ height: '70px', objectFit: 'contain' }}
          />
          
          <button
            onClick={() => setShowInstructions(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              backgroundColor: '#3730a3',
              border: 'none',
              borderRadius: '12px',
              color: '#e9d5ff',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            <Info size={20} />
            <span>Como Instalar</span>
          </button>
        </div>
      </header>

      {/* Info dispositivos */}
      <section style={{
        padding: '24px 20px',
        borderBottom: '1px solid #1e1b4b',
        textAlign: 'center',
        background: 'linear-gradient(to bottom, rgba(124, 58, 237, 0.1), transparent)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '40px',
          marginBottom: '12px'
        }}>
          <div style={{ textAlign: 'center', color: '#a78bfa' }}>
            <Tv size={32} />
            <p style={{ fontSize: '14px', marginTop: '4px' }}>TV Box</p>
          </div>
          <div style={{ textAlign: 'center', color: '#a78bfa' }}>
            <Monitor size={32} />
            <p style={{ fontSize: '14px', marginTop: '4px' }}>Smart TV</p>
          </div>
          <div style={{ textAlign: 'center', color: '#a78bfa' }}>
            <Smartphone size={32} />
            <p style={{ fontSize: '14px', marginTop: '4px' }}>Android</p>
          </div>
        </div>
        <p style={{ color: '#9ca3af', fontSize: '16px' }}>
          Use <span style={{ color: 'white' }}>↑↓</span> para navegar • <span style={{ color: 'white' }}>OK</span> para baixar
        </p>
      </section>

      {/* Conteúdo */}
      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ fontSize: '24px', color: '#a78bfa' }}>Carregando...</p>
          </div>
        ) : (
          <>
            {/* App Destaque - Cardoso TV */}
            {featuredApp && (
              <FeaturedAppCard 
                app={featuredApp}
                isFocused={focusedIndex === 0}
                onFocus={setFocusedIndex}
              />
            )}
            
            {/* Separador */}
            {otherApps.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                margin: '32px 0 24px'
              }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#3730a3' }}></div>
                <span style={{ color: '#8b5cf6', fontSize: '14px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  Mais Apps
                </span>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#3730a3' }}></div>
              </div>
            )}
            
            {/* Outros Apps */}
            {otherApps.map((app, idx) => (
              <AppCard 
                key={app.slug}
                app={app}
                index={idx + 1}
                isFocused={focusedIndex === idx + 1}
                onFocus={setFocusedIndex}
              />
            ))}
          </>
        )}
      </main>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#0a0715',
        borderTop: '1px solid #1e1b4b',
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        <img 
          src={CARDOSO_LOGO_URL} 
          alt="Cardoso Tech" 
          style={{ height: '60px', marginBottom: '20px' }}
        />
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          © 2025 Cardoso Tech - Todos os direitos reservados
        </p>
      </footer>

      <InstructionsModal 
        isOpen={showInstructions} 
        onClose={() => setShowInstructions(false)} 
      />
    </div>
  );
};

export default CardosoPage;
